/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import React, {
  useState,
  useEffect,
  ReactElement,
  useContext,
  useRef,
  useMemo,
} from "react";
import { Badge } from "@chakra-ui/react";
import { Box, CircularProgress } from "@mui/material";
import { AgGridReact } from "ag-grid-react";
import {
  ColDef,
  ICellRendererParams,
  ModuleRegistry,
  AllCommunityModule,
  themeAlpine,
} from "ag-grid-community";
import axios from "axios";
import Card from "components/card";
import { usePageWebSocket } from "../../../hooks/usePageWebSocket";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { ImSpinner8 } from "react-icons/im";
import { Tooltip, IconButton } from "@mui/material";
import { ProfileContext } from "../../../store/ProfileProvider";
import DisplayAmount from "../shared/DisplayAmount";
import { MdOutlineContentCopy } from "react-icons/md";
import { FaArrowTrendUp, FaArrowTrendDown } from "react-icons/fa6";

dayjs.extend(utc);
dayjs.extend(timezone);

ModuleRegistry.registerModules([AllCommunityModule]);

interface PayloadType {
  [key: string]: any;
}

type Merchant = {
  id: string;
  name: string;
};

type Subaccount = {
  id: string;
  name: string;
};

type Receiver = {
  id: string;
  firs_tname: string;
  last_name: string;
  email: string;
};

type ReceiverAccount = {
  id: string;
  email: string;
  bsb: string;
  bank_code: string;
  account_number: string;
};

type PayoutProvider = {
  id: string;
  name: string;
};

type RowData = {
  id: string;
  status: string;
  amount: string;
  asset: string;
  created_at: string;
  note: string;
  merchant: Merchant;
  subaccount: Subaccount;
  transactions: any[];
  receiver: Receiver;
  receiver_account: ReceiverAccount;
  payout_provider: PayoutProvider;
  batch_id: string;
  cause: any;
  crypto: boolean;
};

type Address = {
  id: string;
  address: string;
  nickname: string;
};

type RowDataTrade = {
  id: string;
  status: string;
  shortid: string;
  from_amount: string;
  from_asset: string;
  to_asset: string;
  to_network: string;
  to_address: Address;
  created_at: string;
  merchant: Merchant;
  subaccount: Subaccount;
  transactions: any[];
  reason?: string;
  transaction_reference?: string;
};

const rowPerPage = 5;

const Dashboard = (userPermissions: any) => {
  const { profileTimeZone } = useContext(ProfileContext);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [tableData, setTableData] = useState([]);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(rowPerPage);
  const [rowCount, setRowCount] = useState(rowPerPage);
  const [tableTradeData, setTableTradeData] = useState([]);
  const [pageTrade, setPageTrade] = useState(0);
  const [pageSizeTrade, setPageSizeTrade] = useState(rowPerPage);
  const [rowTradeCount, setRowTradeCount] = useState(rowPerPage);
  const [totalPayoutAmountPast30Days, setTotalPayoutAmountPast30Days] =
    useState<any>(null);
  const userRole = localStorage.getItem("userRole")?.toLowerCase();
  const columnMinWidth = userRole !== "superadmin" ? 350 : 250;

  // AbortController for cancelling requests on unmount
  const abortControllerRef = useRef<AbortController | null>(null);
  // Request ID to track the latest request and prevent stale updates
  const requestIdRef = useRef(0);

  const { received } = usePageWebSocket("payout_list");

  // Cleanup: Cancel all pending requests when component unmounts
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Create new AbortController for each batch of requests
  const getAbortSignal = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    return abortControllerRef.current.signal;
  };

  interface StatusItems {
    status: string;
    value: string;
    display: ReactElement;
  }

  const statusClasses = "status";

  const statusList: StatusItems[] = [
    {
      status: "awaiting",
      value: "awaiting",
      display: (
        <Badge className={`${statusClasses} status-awaiting`}>Awaiting</Badge>
      ),
    },
    {
      status: "sent_mail",
      value: "sent_mail",
      display: (
        <Badge className={`${statusClasses} status-sent_mail`}>
          Email Sent
        </Badge>
      ),
    },
    {
      status: "pending",
      value: "pending",
      display: (
        <Badge className={`${statusClasses} status-pending`}>Pending</Badge>
      ),
    },
    {
      status: "unassessed",
      value: "unassessed",
      display: (
        <Badge className={`${statusClasses} status-unassessed`}>
          Unassessed
        </Badge>
      ),
    },
    {
      status: "in_progress",
      value: "in_progress",
      display: (
        <Badge className={`${statusClasses} status-in_progress`}>
          In Progress
        </Badge>
      ),
    },
    {
      status: "successful",
      value: "successful",
      display: (
        <Badge className={`${statusClasses} status-successful`}>
          Completed
        </Badge>
      ),
    },
    {
      status: "completed",
      value: "completed",
      display: (
        <Badge className={`${statusClasses} status-completed`}>Completed</Badge>
      ),
    },
    {
      status: "cancelled",
      value: "cancelled",
      display: (
        <Badge className={`${statusClasses} status-cancelled`}>Cancelled</Badge>
      ),
    },
    {
      status: "failed",
      value: "failed",
      display: (
        <Badge className={`${statusClasses} status-failed`}>Failed</Badge>
      ),
    },
    {
      status: "rejected",
      value: "rejected",
      display: (
        <Badge className={`${statusClasses} status-rejected`}>Rejected</Badge>
      ),
    },
    {
      status: "successful_pending_kyc",
      value: "successful_pending_kyc",
      display: (
        <Badge className={`${statusClasses} status-successful_pending_kyc`}>
          Successful Pending KYC
        </Badge>
      ),
    },
  ];

  // Silent reload on WebSocket messages
  useEffect(() => {
    if (!received) return;

    // Handle individual payout cancel — update row status directly
    if (received.messageType === "payout") {
      const payoutId = received.data?.payout_id;
      const newStatus = received.data?.status;
      if (payoutId && newStatus) {
        setTableData((prev: any) => prev.map((row: any) =>
          row.id === payoutId ? { ...row, status: newStatus } : row
        ));
      }
      return;
    }

    if (received.messageType !== "batch") return;
    if (received.data?.processing) return;

    const signal = getAbortSignal();
    const currentRequestId = ++requestIdRef.current;
    fetchLastestRecord({ page: 0, limit: rowPerPage }, signal, currentRequestId);
  }, [received]);

  useEffect(() => {
    const signal = getAbortSignal();
    // Increment request ID to invalidate any previous requests
    const currentRequestId = ++requestIdRef.current;

    const fetchData = async () => {
      setIsLoading(true);
      await fetchLastestRecord(
        {
          page: page,
          limit: rowPerPage,
        },
        signal,
        currentRequestId
      );
      if (signal.aborted || requestIdRef.current !== currentRequestId) return;
      setIsLoading(false);
    };

    fetchData();
  }, []);

  const fetchLastestRecord = async (
    payload: PayloadType = {},
    signal?: AbortSignal,
    requestId?: number
  ) => {
    try {
      const queryObject: PayloadType = {
        limit: payload?.limit,
        usertype: userRole === "superadmin" ? "a" : "m",
      };

      const queryString = Object.keys(queryObject)
        .map(
          (key) =>
            `${encodeURIComponent(key)}=${encodeURIComponent(queryObject[key])}`
        )
        .join("&");

      const response = await axios.get(
        process.env.REACT_APP_API_URL +
          `/api/payout/get-all-payouts?${queryString}`,
        { signal }
      );

      // Don't update state if request was aborted or a newer request was started
      if (signal?.aborted) return;
      if (requestId !== undefined && requestIdRef.current !== requestId) return;

      const newData = response.data.payouts?.payload || [];

      setTotalPayoutAmountPast30Days(
        response.data.payouts?.totalpayoutamount_last_thirtydays || []
      );

      // const responseTrade = await axios.get(
      //   process.env.REACT_APP_API_URL +
      //   `/api/trade/trade-history?${queryString}`
      // );

      setRowCount(newData.length);

      if (newData && newData.length !== 0) {
        setTableData(newData);
      } else {
        setTableData([]);
      }

      // let newDataTrade = responseTrade.data.trades?.payload;

      // setRowTradeCount(newDataTrade.length);

      // if (newDataTrade && newDataTrade.length !== 0) {
      //   setTableTradeData(newDataTrade);
      // } else {
      //   setTableTradeData([]);
      // }
    } catch (error: any) {
      if (error.name === "CanceledError" || error.name === "AbortError") {
        return;
      }
      // Don't update state if a newer request was started
      if (requestId !== undefined && requestIdRef.current !== requestId) return;
      setTableData([]);
      setTableTradeData([]);
      setIsLoading(false);
      console.error("Error fetching data", error);
    }
  };

  const DateRenderer = (params: ICellRendererParams) => (
    <>{formatDate(params.data.created_at)}</>
  );

  const MerchantRenderer = (params: ICellRendererParams) => (
    <>{params.data.merchant?.name}</>
  );

  const SubaccountRenderer = (params: ICellRendererParams) => (
    <>{params.data.subaccount?.name}</>
  );

  const AmountRenderer = (params: ICellRendererParams) => (
    <>
      {params.data.crypto ? (
        params.data.amount
      ) : (
        <DisplayAmount
          amount={Number(params.data.amount)}
          currency={params.data.asset}
        />
      )}
      &nbsp;
      {params.data.asset}
    </>
  );

  const StatusRenderer = (params: ICellRendererParams) => {
    const status = params.data.status;
    const statusItem = statusList.find((item) => item.status === status);
    const failMessage = params.data.cause?.fail?.message || (typeof params.data.cause?.fail === "string" ? params.data.cause.fail : null);
    return (
      <div style={{ width: "100%", display: "flex", justifyContent: "flex-end" }}>
        {userRole === "superadmin" &&
        status === "failed" &&
        failMessage ? (
          <Tooltip title={failMessage} arrow placement="top">
            <div className="cursor-help">
              {statusItem ? statusItem.display : null}
            </div>
          </Tooltip>
        ) : (
          <>{statusItem ? statusItem.display : null}</>
        )}
      </div>
    );
  };

  const columns: ColDef[] = [
    {
      field: "created_at",
      headerName: "Date",
      flex: 1,
      minWidth: 180,
      sortable: false,
      cellRenderer: DateRenderer,
    },
    {
      field: "merchant",
      headerName: "Merchant",
      flex: 1,
      sortable: false,
      minWidth: 160,
      cellRenderer: MerchantRenderer,
    },
    {
      field: "subaccount",
      headerName: "",
      flex: 1,
      sortable: false,
      minWidth: 160,
      cellRenderer: SubaccountRenderer,
    },
    {
      field: "amount",
      headerName: "Amount",
      sortable: false,
      minWidth: 100,
      flex: 1,
      cellRenderer: AmountRenderer,
      headerClass: "ag-right-aligned-header",
      cellStyle: { display: "flex", alignItems: "center", justifyContent: "flex-end" },
    },
    {
      field: "note",
      headerName: "Description",
      flex: 1,
      sortable: false,
      minWidth: 280,
    },
    {
      field: "status",
      headerName: "",
      sortable: false,
      width: 140,
      resizable: false,
      cellRenderer: StatusRenderer,
      cellClass: "ag-cell-status",
    },
  ];

  const filterColumns = () => {
    let rebuildColumns = [];
    if (userRole === "superadmin" || userRole === "merchant") {
      const excludeFields = userRole === "merchant" ? ["merchant"] : [];
      rebuildColumns = columns.filter(
        (column) => !excludeFields.includes(column?.field)
      );
    } else {
      const excludeFields = ["merchant", "subaccount"];
      rebuildColumns = columns.filter(
        (column) => !excludeFields.includes(column?.field)
      );
    }
    return rebuildColumns;
  };

  const columnsTrade: ColDef[] = [
    {
      field: "created_at",
      headerName: "Created Date",
      sortable: false,
      flex: 1,
      minWidth: 180,
      cellRenderer: (params: ICellRendererParams) => (
        <>{formatDate(params.data.created_at)}</>
      ),
    },
    {
      field: "merchant",
      headerName: "Merchant",
      flex: 1,
      sortable: false,
      minWidth: 160,
      cellRenderer: (params: ICellRendererParams) => (
        <>{params.data.merchant?.name}</>
      ),
    },
    {
      field: "subaccount",
      headerName: "",
      sortable: false,
      flex: 1,
      minWidth: 160,
      cellRenderer: (params: ICellRendererParams) => (
        <>{params.data.subaccount?.name}</>
      ),
    },
    {
      field: "from_asset",
      headerName: "Pair",
      minWidth: 120,
      flex: 1,
      sortable: false,
      cellStyle: { display: "flex", alignItems: "center", justifyContent: "center" },
      cellRenderer: (params: ICellRendererParams) =>
        `${params.data.from_asset}/${params.data.to_asset} (${params.data.to_network})`,
    },
    {
      field: "from_amount",
      headerName: "Amount",
      minWidth: 120,
      flex: 1,
      sortable: false,
      cellStyle: { display: "flex", alignItems: "center", justifyContent: "flex-end" },
      cellRenderer: (params: ICellRendererParams) => (
        <DisplayAmount
          amount={Number(params.data.from_amount)}
          currency={params.data.from_asset}
        />
      ),
    },
    {
      field: "to_address",
      headerName: "To Address",
      minWidth: 350,
      flex: 1,
      sortable: false,
      cellRenderer: (params: ICellRendererParams) =>
        `${params.data.to_address?.nickname} (${formatAddress(
          params.data.to_address?.address
        )})`,
    },
    {
      field: "status",
      headerName: "",
      sortable: false,
      minWidth: 140,
      flex: 1,
      cellClass: "ag-cell-status",
      cellRenderer: (params: ICellRendererParams) => {
        const status = params.data.status;
        const statusItem = statusList.find((item) => item.status === status);
        return (
          <>
            {userRole === "superadmin" &&
            ((status === "completed" && params.data.transaction_reference) ||
              (status === "rejected" && params.data.reason)) ? (
              <Tooltip
                title={
                  status === "completed"
                    ? "Ref. " + params.data.transaction_reference
                    : "Reason: " + params.data.reason
                }
                arrow
                placement="top"
              >
                <div className="cursor-help">
                  {statusItem ? statusItem.display : null}
                </div>
              </Tooltip>
            ) : statusItem ? (
              statusItem.display
            ) : null}
          </>
        );
      },
    },
  ];

  const filterColumnsTrade = () => {
    let rebuildColumns = [];
    if (userRole === "superadmin" || userRole === "merchant") {
      const excludeFields = userRole === "merchant" ? ["merchant"] : [];
      rebuildColumns = columnsTrade.filter(
        (column) => !excludeFields.includes(column?.field)
      );
    } else {
      const excludeFields = ["merchant", "subaccount"];
      rebuildColumns = columnsTrade.filter(
        (column) => !excludeFields.includes(column?.field)
      );
    }
    return rebuildColumns;
  };

  const handleClickCopy = async (tradeId: string) => {
    try {
      await navigator.clipboard.writeText(tradeId);
    } catch (err) {
      console.error("Failed to copy trade ID: ", err);
    }
  };

  const formatDate = (timestamp: string) => {
    return dayjs(Number(timestamp)).tz(profileTimeZone).format("DD MMM YY");
  };

  const formatAddress = (address?: string): string => {
    if (!address || address.length < 24) return "N/A";
    return `${address.slice(0, 15)}...${address.slice(-9)}`;
  };

  const defaultColDef = useMemo<ColDef>(
    () => ({
      resizable: true,
      suppressMenu: true,
    }),
    []
  );

  const CustomLoadingOverlay = () => (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
      <CircularProgress size={40} />
    </div>
  );

  return (
    <div>
      {/* <Card className="rounded-lg bg-white p-6 shadow-md">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h3 className="text-xl font-bold text-navy-700 dark:text-white">
              Total Payouts
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Past 30 days
            </p>
          </div>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-10">
            <ImSpinner8 className="spinner text-primary-500" size={32} />
          </div>
        ) : !totalPayoutAmountPast30Days || totalPayoutAmountPast30Days.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 text-gray-500">
            <p className="text-sm">No payouts in the past 30 days</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-4 xl:grid-cols-4">
            {totalPayoutAmountPast30Days.map((item: any) => {
              const assetCode = item.asset.toLowerCase().includes('-')
                ? item.asset.toLowerCase().split('-')[0]
                : item.asset.toLowerCase();

              return (
                <div
                  key={item.asset}
                  className="hover:border-emerald-200 group relative rounded-lg border border-gray-200 p-4 transition-all hover:shadow-md"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <img
                        src={`/images/icons/${assetCode}.png`}
                        alt={item.asset}
                        width="20px"
                      />
                      <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                        {item.asset}
                      </span>
                    </div>
                  </div>
                  <div className="mt-1 text-3xl font-bold text-primary-600">
                    {item.crypto ? item.amount : <DisplayAmount amount={Number(item.amount)} currency={item.asset} />}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card> */}

      <div className="mb-3 flex items-baseline gap-2">
        <h3 className="text-xl font-semibold text-navy-700 dark:text-white">
          Total Payouts
        </h3>
        <p className="text-sm text-gray-400 dark:text-gray-400">(past 30 days)</p>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-10">
          <ImSpinner8 className="spinner text-primary-500" size={28} />
        </div>
      ) : totalPayoutAmountPast30Days && totalPayoutAmountPast30Days.length > 0 ? (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5">
          {totalPayoutAmountPast30Days.map((item: any) => {
            const assetCode = item.asset.toLowerCase().includes("-")
              ? item.asset.toLowerCase().split("-")[0]
              : item.asset.toLowerCase();

            return (
              <div
                className="
                  group relative cursor-pointer rounded-xl bg-white
                  p-3 shadow-sm ring-1
                  ring-gray-200 transition-all duration-200
                  hover:-translate-y-[2px] hover:shadow-lg
                  hover:ring-primary-500
                  sm:p-4
                  md:p-5
                "
              >
                <div className="flex items-center gap-2 sm:gap-3">
                  <span
                    className="
                      flex h-8 w-8 items-center justify-center rounded-full bg-gray-100
                      ring-1 ring-gray-200 sm:h-9 sm:w-9 md:h-10 md:w-10
                    "
                  >
                    <img
                      src={`/images/icons/${assetCode}.png`}
                      alt={item.asset}
                      className="sm:h-4.5 sm:w-4.5 h-4 w-4 md:h-5 md:w-5"
                    />
                  </span>

                  <span className="text-xs font-semibold text-navy-700 sm:text-sm">
                    {item.asset}
                  </span>
                </div>

                <div
                  className="
                    mt-2 text-lg font-semibold
                    tracking-tight text-navy-900 sm:mt-3
                    sm:text-xl md:mt-4 md:text-2xl
                  "
                >
                  {item.crypto ? (
                    item.amount
                  ) : (
                    <DisplayAmount
                      amount={Number(item.amount)}
                      currency={item.asset}
                    />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="mb-10 flex flex-col items-center justify-center py-10 text-gray-500">
          <p className="text-sm">No payouts in the past 30 days</p>
        </div>
      )}

      <div className="mb-3 mt-8 flex items-center justify-between">
        <h3 className="text-xl font-semibold text-navy-700 dark:text-white">
          Latest Payouts
        </h3>
      </div>
      <Box className="card mb-6 w-full">
        <AgGridReact
          theme={themeAlpine}
          rowData={isLoading ? [] : tableData}
          columnDefs={filterColumns()}
          defaultColDef={defaultColDef}
          getRowId={(params) => params.data.id}
          rowHeight={48}
          headerHeight={44}
          domLayout="autoHeight"
          suppressPaginationPanel={true}
          suppressDragLeaveHidesColumns={true}
          enableCellTextSelection={true}
          ensureDomOrder={true}
          loading={isLoading}
          loadingOverlayComponent={CustomLoadingOverlay}
          overlayNoRowsTemplate="No data available"
        />
      </Box>

      {/* Trade History */}
      {/* <div className="mb-5 mt-6 flex flex-wrap pl-3">
        <h3 className="text-lg font-bold text-navy-700 dark:text-white">
          Latest Trades
        </h3>
      </div>
      <Box className="card w-full">
        <AgGridReact
          theme={themeAlpine}
          rowData={isLoading ? [] : tableTradeData}
          columnDefs={filterColumnsTrade()}
          defaultColDef={defaultColDef}
          getRowId={(params) => params.data.id}
          rowHeight={48}
          headerHeight={44}
          domLayout="autoHeight"
          suppressPaginationPanel={true}
          loading={isLoading}
          loadingOverlayComponent={CustomLoadingOverlay}
          overlayNoRowsTemplate="No data available"
        />
      </Box> */}
    </div>
  );
};

export default Dashboard;
